import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Loader2, Plus, Trash2, UserRound } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { FieldLabel } from '@/components/common/FieldLabel';
import { PageHeader } from '@/components/layout/PageHeader';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { formatNumber, normalizeWholeNumberText, parseNumberText, pluralize } from '@/utils/number';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { expenseTypeQueryOptions } from '../api/billing.api';
import { MAX_WEB_EXPENSE_LINES } from '../constants/billing.constants';
import { useCreateWebExpense } from '../hooks/useCreateWebExpense';
import type { WebExpenseLinePayload } from '../types/billing.types';
import { defaultDateTo } from '../utils/period';

const LINE_FIELDS_GRID_CLASS =
  'grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_8rem]';
const COMMENT_ROW_COUNT = 2;

interface DraftLine {
  key: number;
  expenseTypeId: number | undefined;
  amount: string;
  fromLocation: string;
  toLocation: string;
  vendor: string;
  comments: string;
}

type LineTextKey = 'fromLocation' | 'toLocation' | 'vendor';

interface LineTextFieldConfig {
  key: LineTextKey;
  idPrefix: string;
  label: string;
  placeholder: string;
  wideOrderClass: string;
}

const LINE_TEXT_FIELDS: LineTextFieldConfig[] = [
  {
    key: 'fromLocation',
    idPrefix: 'web-expense-from',
    label: 'From Location',
    placeholder: 'Start point',
    wideOrderClass: '@3xl:order-2',
  },
  {
    key: 'toLocation',
    idPrefix: 'web-expense-to',
    label: 'To Location',
    placeholder: 'End point',
    wideOrderClass: '@3xl:order-3',
  },
  {
    key: 'vendor',
    idPrefix: 'web-expense-vendor',
    label: 'Vendor / Person',
    placeholder: 'Shop or person name',
    wideOrderClass: '@3xl:order-4',
  },
];

const emptyLine = (key: number): DraftLine => ({
  key,
  expenseTypeId: undefined,
  amount: '',
  fromLocation: '',
  toLocation: '',
  vendor: '',
  comments: '',
});

interface LineProblem {
  expenseType?: string;
  fromLocation?: string;
  toLocation?: string;
  vendor?: string;
  amount?: string;
  comments?: string;
}

function checkLine(line: DraftLine): LineProblem {
  const problem: LineProblem = {};

  if (line.expenseTypeId === undefined) {
    problem.expenseType = 'Pick a type';
  }
  if (line.fromLocation.trim() === '') {
    problem.fromLocation = 'Enter the from location';
  }
  if (line.toLocation.trim() === '') {
    problem.toLocation = 'Enter the to location';
  }
  if (line.vendor.trim() === '') {
    problem.vendor = 'Enter the vendor or person';
  }

  const amount = parseNumberText(line.amount);
  if (amount === undefined) {
    problem.amount = 'Enter the amount';
  } else if (amount <= 0) {
    problem.amount = 'Must be more than 0';
  }

  if (line.comments.trim() === '') {
    problem.comments = 'Enter a comment';
  }

  return problem;
}

const isComplete = (problem: LineProblem): boolean => Object.keys(problem).length === 0;

function toLinePayload(line: DraftLine): WebExpenseLinePayload | undefined {
  const amount = parseNumberText(line.amount);
  if (line.expenseTypeId === undefined || amount === undefined) return undefined;

  return {
    expenseTypeId: line.expenseTypeId,
    amount,
    fromLocation: line.fromLocation.trim(),
    toLocation: line.toLocation.trim(),
    vendor: line.vendor.trim(),
    comments: line.comments.trim(),
  };
}

interface LineFieldProps {
  id: string;
  label: string;
  problem: string | undefined;
  warning?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}

function LineField({ id, label, problem, warning, className, children }: LineFieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <FieldLabel htmlFor={id} isRequired>
        {label}
      </FieldLabel>
      {children}
      {problem ? <p className="text-xs text-destructive">{problem}</p> : null}
      {!problem && warning ? <p className="text-xs text-amber-600">{warning}</p> : null}
    </div>
  );
}

interface SavedSummary {
  lineCount: number;
  total: number;
}

export function WebExpensePage() {
  const { data: expenseTypes, isLoading: expenseTypesLoading } =
    useQuery(expenseTypeQueryOptions());

  const signedInName = useAuthStore((state) => state.user?.fullName);
  const [expenseDate, setExpenseDate] = useState(defaultDateTo());
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(0)]);
  const [nextKey, setNextKey] = useState(1);
  const [saved, setSaved] = useState<SavedSummary | null>(null);
  const [showProblems, setShowProblems] = useState(false);

  const typeOptions = useMemo(
    () =>
      (expenseTypes ?? []).map((type) => ({
        id: type.id,
        name: type.code ? `${type.name} (${type.code})` : type.name,
      })),
    [expenseTypes],
  );

  const problems = lines.map(checkLine);
  const completeCount = problems.filter(isComplete).length;
  const total = lines.reduce((sum, line) => sum + (parseNumberText(line.amount) ?? 0), 0);
  const canSubmit = completeCount === lines.length;
  const hasMissingFields = showProblems && !canSubmit;
  const isAtLineLimit = lines.length >= MAX_WEB_EXPENSE_LINES;

  const duplicateTypeIds = useMemo(() => {
    const counts = new Map<number, number>();
    for (const line of lines) {
      if (line.expenseTypeId === undefined) continue;
      counts.set(line.expenseTypeId, (counts.get(line.expenseTypeId) ?? 0) + 1);
    }
    return new Set(
      [...counts.entries()].filter(([, count]) => count > 1).map(([typeId]) => typeId),
    );
  }, [lines]);

  const createExpense = useCreateWebExpense(() => {
    setSaved({ lineCount: lines.length, total });
    setLines([emptyLine(nextKey)]);
    setNextKey((key) => key + 1);
    setShowProblems(false);
  });

  function updateLine(key: number, patch: Partial<DraftLine>) {
    setSaved(null);
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function updateLineText(key: number, field: LineTextKey, value: string) {
    setSaved(null);
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, [field]: value } : line)));
  }

  function handleAmountChange(key: number, typed: string) {
    const amount = normalizeWholeNumberText(typed);
    if (amount === undefined) return;
    updateLine(key, { amount });
  }

  function handleExpenseDateChange(value: string) {
    setSaved(null);
    setExpenseDate(value);
  }

  function addLine() {
    setSaved(null);
    setLines((prev) => [...prev, emptyLine(nextKey)]);
    setNextKey((key) => key + 1);
  }

  function duplicateLine(key: number) {
    setSaved(null);
    setLines((prev) => {
      const source = prev.find((line) => line.key === key);
      if (!source) return prev;
      const index = prev.indexOf(source);
      const copy: DraftLine = { ...source, key: nextKey };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setNextKey((key) => key + 1);
  }

  function removeLine(key: number) {
    setSaved(null);
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((line) => line.key !== key)));
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setShowProblems(true);
    if (!canSubmit) return;

    const payloadLines: WebExpenseLinePayload[] = [];
    for (const line of lines) {
      const payloadLine = toLinePayload(line);
      if (!payloadLine) return;
      payloadLines.push(payloadLine);
    }

    createExpense.mutate({ expenseDate, lines: payloadLines });
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Web Expense Info"
        actions={
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {signedInName ? (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <UserRound className="size-4" aria-hidden="true" />
                Saving for
                <span className="font-medium text-foreground">{signedInName}</span>
              </span>
            ) : null}
            <div className="flex items-center gap-2">
              <Label htmlFor="web-expense-date" className="whitespace-nowrap text-muted-foreground">
                Expense Date
              </Label>
              <DatePickerInput
                id="web-expense-date"
                value={expenseDate}
                maxDate={defaultDateTo()}
                onChange={handleExpenseDateChange}
              />
            </div>
          </div>
        }
      />

      {saved ? (
        <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          <span
            className={cn(
              'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
              ACCENT_MARK_CLASS,
            )}
          >
            <Check className="size-4" />
          </span>
          <span>
            Saved {saved.lineCount} {pluralize(saved.lineCount, 'expense')} totalling{' '}
            <span className="font-medium tabular-nums">{formatNumber(saved.total)} Tk</span>. It is
            now waiting to be verified.
          </span>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" aria-label="Web expense entry">
        {lines.map((line, index) => {
          const problem: LineProblem = showProblems ? (problems[index] ?? {}) : {};
          const lineNumber = index + 1;
          const typeId = `web-expense-type-${line.key}`;
          const amountId = `web-expense-amount-${line.key}`;
          const commentsId = `web-expense-comments-${line.key}`;
          const isDuplicateType =
            line.expenseTypeId !== undefined && duplicateTypeIds.has(line.expenseTypeId);

          return (
            <section
              key={line.key}
              aria-label={`Expense ${lineNumber}`}
              className="@container rounded-xl border bg-card shadow-sm"
            >
              <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={cn(
                      'inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
                      ACCENT_MARK_CLASS,
                    )}
                  >
                    {lineNumber}
                  </span>
                  Expense
                </span>
                <div className="flex items-center gap-1">
                  <span className="mr-2 text-sm font-semibold tabular-nums">
                    {formatNumber(parseNumberText(line.amount) ?? 0)} Tk
                  </span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-10"
                    disabled={isAtLineLimit}
                    aria-label={`Copy expense ${lineNumber}`}
                    title="Copy expense"
                    onClick={() => duplicateLine(line.key)}
                  >
                    <Copy className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-10 text-destructive"
                    disabled={lines.length === 1}
                    aria-label={`Remove expense ${lineNumber}`}
                    title="Remove expense"
                    onClick={() => removeLine(line.key)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-3 p-4">
                <div className={LINE_FIELDS_GRID_CLASS}>
                  <LineField
                    id={typeId}
                    label="Expense Type"
                    problem={problem.expenseType}
                    warning={
                      isDuplicateType
                        ? 'Same type twice on one day - approval allows only one'
                        : undefined
                    }
                    className="@3xl:order-1"
                  >
                    <OptionSelect
                      id={typeId}
                      options={typeOptions}
                      isLoading={expenseTypesLoading}
                      value={line.expenseTypeId}
                      onChange={(expenseTypeId) => updateLine(line.key, { expenseTypeId })}
                      placeholder="Select a type"
                      clearable
                      aria-invalid={problem.expenseType !== undefined}
                    />
                  </LineField>

                  <LineField
                    id={amountId}
                    label="Amount (Tk)"
                    problem={problem.amount}
                    className="@3xl:order-5"
                  >
                    <Input
                      id={amountId}
                      inputMode="numeric"
                      placeholder="0"
                      className="h-10 text-right tabular-nums"
                      value={line.amount}
                      aria-invalid={problem.amount !== undefined}
                      onChange={(event) => handleAmountChange(line.key, event.target.value)}
                    />
                  </LineField>

                  {LINE_TEXT_FIELDS.map((field) => {
                    const fieldId = `${field.idPrefix}-${line.key}`;
                    return (
                      <LineField
                        key={field.key}
                        id={fieldId}
                        label={field.label}
                        problem={problem[field.key]}
                        className={field.wideOrderClass}
                      >
                        <Input
                          id={fieldId}
                          value={line[field.key]}
                          placeholder={field.placeholder}
                          className="h-10"
                          aria-invalid={problem[field.key] !== undefined}
                          onChange={(event) =>
                            updateLineText(line.key, field.key, event.target.value)
                          }
                        />
                      </LineField>
                    );
                  })}
                </div>

                <LineField id={commentsId} label="Comment" problem={problem.comments}>
                  <Textarea
                    id={commentsId}
                    rows={COMMENT_ROW_COUNT}
                    value={line.comments}
                    placeholder="What it was for"
                    aria-invalid={problem.comments !== undefined}
                    onChange={(event) => updateLine(line.key, { comments: event.target.value })}
                  />
                </LineField>
              </div>
            </section>
          );
        })}

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full gap-2 border-2 border-dashed text-muted-foreground hover:border-primary hover:text-primary"
          disabled={isAtLineLimit}
          onClick={addLine}
        >
          <Plus className="size-4" />
          {isAtLineLimit
            ? `You can add up to ${MAX_WEB_EXPENSE_LINES} expenses at a time`
            : 'New expense'}
        </Button>

        <div className="flex flex-col gap-3 rounded-xl border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span
            className={cn(
              'text-sm',
              hasMissingFields ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {hasMissingFields
              ? 'Fill in the fields marked in red'
              : `${completeCount} of ${lines.length} ${pluralize(lines.length, 'expense')} complete`}
          </span>
          <div className="flex items-center justify-between gap-4 sm:justify-end">
            <span className="flex items-baseline gap-2">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="text-lg font-semibold tabular-nums">{formatNumber(total)} Tk</span>
            </span>
            <Button type="submit" disabled={createExpense.isPending}>
              {createExpense.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving
                </>
              ) : (
                'Save expense'
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
